import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { ActivityLogEntry } from '@/types/sharing';

const getActivityIcon = (eventType: string): string => {
  const iconMap: Record<string, string> = {
    'case_shared_user': '📁',
    'case_shared_firm': '🏢',
    'chat_shared_user': '💬',
    'chat_shared_firm': '🗨️',
    'share_revoked': '🔴',
    'member_invited': '📧',
    'member_accepted': '✅',
    'member_removed': '🟡',
    'share_level_changed': '🔧',
  };
  return iconMap[eventType] || '📌';
};

const getActivityColor = (eventType: string): string => {
  const colorMap: Record<string, string> = {
    'case_shared_user': 'text-blue-600',
    'case_shared_firm': 'text-blue-700',
    'chat_shared_user': 'text-green-600',
    'chat_shared_firm': 'text-green-700',
    'share_revoked': 'text-red-600',
    'member_invited': 'text-purple-600',
    'member_accepted': 'text-emerald-600',
    'member_removed': 'text-yellow-600',
    'share_level_changed': 'text-orange-600',
  };
  return colorMap[eventType] || 'text-gray-600';
};

const getActivityDescription = (entry: any): string => {
  const actor = entry.actorName || 'Alguém';
  const targetName = entry.targetName || 'um usuário';
  const resourceTitle = entry.metadata?.caso_title || entry.metadata?.conversation_title || 'um recurso';
  
  switch (entry.event_type) {
    case 'case_shared_user':
      return `${actor} compartilhou o caso "${resourceTitle}" com ${targetName}`;
    case 'case_shared_firm':
      return `${actor} compartilhou o caso "${resourceTitle}" com o escritório`;
    case 'chat_shared_user':
      return `${actor} compartilhou o chat "${resourceTitle}" com ${targetName}`;
    case 'chat_shared_firm':
      return `${actor} compartilhou o chat "${resourceTitle}" com o escritório`;
    case 'share_revoked':
      return `${actor} revogou compartilhamento de ${resourceTitle}`;
    case 'member_invited':
      return `${actor} convidou ${targetName} para o escritório`;
    case 'member_accepted':
      return `${actor} aceitou o convite`;
    case 'member_removed':
      return `${actor} removeu ${targetName}`;
    case 'share_level_changed':
      return `${actor} alterou permissões de ${resourceTitle}`;
    default:
      return `${actor} realizou ${entry.event_type}`;
  }
};

export function useFirmActivity(limit: number = 50) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['firm-activity', user?.id, limit],
    queryFn: async (): Promise<ActivityLogEntry[]> => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await (supabase as any)
        .from('firm_activity_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      const activities: ActivityLogEntry[] = [];

      for (const entry of data || []) {
        const { data: actorUser } = await supabase
          .from('users')
          .select('display_name, email')
          .eq('id', entry.actor_id)
          .single();

        const actorName = actorUser?.display_name || actorUser?.email || 'Desconhecido';

        // Get target user name if exists
        let targetName = '';
        if (entry.target_id) {
          const { data: targetUser } = await supabase
            .from('users')
            .select('display_name, email')
            .eq('id', entry.target_id)
            .single();
          targetName = targetUser?.display_name || targetUser?.email || 'usuário';
        }

        activities.push({
          id: entry.id,
          eventType: entry.event_type,
          resourceType: entry.resource_type,
          resourceId: entry.resource_id,
          actorName,
          actorEmail: actorUser?.email || '',
          metadata: entry.metadata || {},
          createdAt: new Date(entry.created_at),
          icon: getActivityIcon(entry.event_type),
          color: getActivityColor(entry.event_type),
          description: getActivityDescription({ ...entry, actorName, targetName }),
        });
      }

      return activities;
    },
    enabled: !!user,
  });
}
